// pages/admin/gift-requests/index.jsx
import { useNavigate, useSearchParams } from "react-router-dom";
import TaleButton from "@components/TaleButton";
import { TaleDashbaordMainCard } from "@components/TaleDashbaordMainCard";
import { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { TaleLoadingFallback } from "@components/TaleLoadingFallback";
import { useQuery } from "@tanstack/react-query";
import { lazily } from "react-lazily";
import { giftRequestsService } from "@/api/awards";
import "./gift-requests.css";
import { FiClock, FiCheckCircle, FiXCircle, FiUser, FiGift } from "react-icons/fi";

const { AdminGiftRequestsList } = lazily(() =>
    import("@/pages/admin/gift-requests/AdminGiftRequestsList")
);
const { InternalServerError } = lazily(() =>
    import("@components/Errors/InternalServerError")
);

const AdminGiftRequests = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const currentPage = parseInt(params.get("page")) || 0;
  const statusFilter = params.get("status") || "PENDING";

  // State for modals
  const [requestToReject, setRequestToReject] = useState(null);
  const [requestToView, setRequestToView] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  const fetchData = useCallback(
      async () => {
        return await giftRequestsService.getGiftRequests(
            currentPage,
            10,
            statusFilter
        );
      },
      [currentPage, statusFilter]
  );

  const {
    isPending: loading,
    data: requestsData,
    error,
    refetch,
  } = useQuery({
    queryKey: ["gift-requests", currentPage, statusFilter],
    queryFn: fetchData,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const [requestItems, setRequestItems] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 0,
    totalItems: 0,
    totalPages: 1
  });

  useEffect(() => {
    if (loading || error || !requestsData) return;

    // Handle API response structure
    if (requestsData.result && Array.isArray(requestsData.result)) {
      setRequestItems(requestsData.result);
    } else if (Array.isArray(requestsData)) {
      setRequestItems(requestsData);
    } else {
      setRequestItems([]);
    }

    // Set pagination from API response
    setPagination({
      currentPage: requestsData.currentPage || currentPage,
      totalItems: requestsData.totalItems || 0,
      totalPages: requestsData.totalPages || 1
    });
  }, [requestsData, loading, error, currentPage]);

  const updateRequest = useCallback(
      (id, props) => {
        const index = requestItems.findIndex((e) => e.id === id);
        if (index === -1) return;
        let clone = { ...requestItems[index], ...props };
        setRequestItems([
          ...requestItems.slice(0, index),
          clone,
          ...requestItems.slice(index + 1),
        ]);
      },
      [requestItems]
  );

  const handleAccept = useCallback(async (id) => {
    setIsProcessing(true);
    try {
      await giftRequestsService.acceptGiftRequest(id);
      updateRequest(id, { status: "ACCEPTED" });
      console.log("Demande acceptée avec succès !");
    } catch (error) {
      console.error("Failed to accept request:", error);
      console.error("Erreur lors de l'acceptation de la demande");
    } finally {
      setIsProcessing(false);
    }
  }, [updateRequest]);

  const handleRejectClick = useCallback((request) => {
    setRequestToReject(request);
    setRejectionReason("");
  }, []);

  const confirmReject = useCallback(async () => {
    if (!requestToReject || !rejectionReason.trim()) {
      console.error("Reason required");
      return;
    }

    setIsProcessing(true);
    try {
      await giftRequestsService.rejectGiftRequest(
          requestToReject.id,
          rejectionReason
      );
      updateRequest(requestToReject.id, {
        status: "REJECTED",
        rejectionReason: rejectionReason
      });
      console.log("Demande rejetée avec succès !");
      setRequestToReject(null);
      setRejectionReason("");
    } catch (error) {
      console.error("Failed to reject request:", error);
      console.error("Erreur lors du rejet de la demande");
    } finally {
      setIsProcessing(false);
    }
  }, [requestToReject, rejectionReason, updateRequest]);

  const cancelReject = useCallback(() => {
    setRequestToReject(null);
    setRejectionReason("");
  }, []);

  const handleViewDetails = useCallback((request) => {
    setRequestToView(request);
  }, []);

  const closeViewModal = useCallback(() => {
    setRequestToView(null);
  }, []);

  const loadingItem = useMemo(
      () => (
          <div className="size-full z-20 absolute flex items-center justify-center grow">
            <TaleLoadingFallback />
          </div>
      ),
      []
  );

  const handleFilterChange = (value) => {
    const params = new URLSearchParams(window.location.search);
    params.set("status", value);
    params.set("page", "0");
    navigate(`/admin/gift-requests?${params.toString()}`);
  };

  const statusOptions = [
    { value: "PENDING", label: "En attente" },
    { value: "ACCEPTED", label: "Acceptées" },
    { value: "REJECTED", label: "Rejetées" },
    { value: "CANCELLED", label: "Annulées" },
    { value: "", label: "Toutes" },
  ];

  // Calculate stats from request items
  const calculateStats = () => {
    return {
      total: requestItems.length,
      pending: requestItems.filter(r => r.status === "PENDING").length,
      accepted: requestItems.filter(r => r.status === "ACCEPTED").length,
      rejected: requestItems.filter(r => r.status === "REJECTED").length,
      cancelled: requestItems.filter(r => r.status === "CANCELLED").length,
      totalPoints: requestItems.reduce((sum, r) => sum + (r.pointsUsed || 0), 0)
    };
  };

  const itemStats = calculateStats();

  return (
      <>
        {/* Rejection Modal */}
        {requestToReject && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Rejeter la demande
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Êtes-vous sûr de vouloir rejeter cette demande ?
                  </p>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Raison du rejet (requise)
                    </label>
                    <textarea
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        rows={3}
                        placeholder="Expliquez pourquoi cette demande est rejetée..."
                    />
                  </div>

                  <div className="flex justify-end gap-3">
                    <TaleButton
                        variant="outlined"
                        onClick={cancelReject}
                        disabled={isProcessing}
                    >
                      Annuler
                    </TaleButton>
                    <TaleButton
                        onClick={confirmReject}
                        loading={isProcessing}
                        disabled={!rejectionReason.trim()}
                        className="bg-red-600 hover:bg-red-700 border-red-600"
                    >
                      Confirmer le rejet
                    </TaleButton>
                  </div>
                </div>
              </div>
            </div>
        )}

        {/* View Details Modal */}
        {requestToView && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  <div className="flex justify-between items-start mb-6">
                    <h3 className="text-xl font-bold text-gray-900">
                      Détails de la demande
                    </h3>
                    <button
                        onClick={closeViewModal}
                        className="text-gray-400 hover:text-gray-600"
                    >
                      <i className="fas fa-times text-xl"></i>
                    </button>
                  </div>

                  <div className="space-y-6">
                    {/* User Info */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Utilisateur</h4>
                      <div className="flex items-center space-x-4">
                        <div className="flex-shrink-0">
                          <div className="h-12 w-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center">
                            <FiUser className="w-6 h-6 text-blue-600" />
                          </div>
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">
                            {requestToView.username}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {requestToView.userId}
                          </div>
                          {requestToView.userEmail && (
                              <div className="text-sm text-gray-500">
                                Email: {requestToView.userEmail}
                              </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Gift Info */}
                    <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-4 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Cadeau demandé</h4>
                      <div className="flex items-center space-x-4">
                        <div className="flex-shrink-0">
                          <div className="h-12 w-12 bg-gradient-to-br from-amber-100 to-orange-200 rounded-lg flex items-center justify-center">
                            <FiGift className="w-6 h-6 text-amber-600" />
                          </div>
                        </div>
                        <div>
                          <div className="font-bold text-gray-900 text-lg">
                            {requestToView.giftName}
                          </div>
                          <div className="text-sm text-gray-600">
                            Type: {requestToView.giftType || "Non spécifié"}
                          </div>
                          <div className="font-medium text-gray-900 mt-1">
                            <span className="text-amber-600">{requestToView.pointsUsed} points</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Request Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-3">
                        <div>
                          <div className="text-sm text-gray-500">Statut</div>
                          <div className="font-medium">
                            {requestToView.status === "PENDING" && (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                                                        <FiClock className="w-4 h-4 mr-1" />
                                                        En attente
                                                    </span>
                            )}
                            {requestToView.status === "ACCEPTED" && (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                                                        <FiCheckCircle className="w-4 h-4 mr-1" />
                                                        Acceptée
                                                    </span>
                            )}
                            {requestToView.status === "REJECTED" && (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                                                        <FiXCircle className="w-4 h-4 mr-1" />
                                                        Rejetée
                                                    </span>
                            )}
                          </div>
                        </div>

                        <div>
                          <div className="text-sm text-gray-500">Date de création</div>
                          <div className="font-medium">
                            {new Date(requestToView.createdAt).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div>
                          <div className="text-sm text-gray-500">Notes</div>
                          <div className="font-medium bg-gray-50 p-3 rounded">
                            {requestToView.notes || "Aucune note"}
                          </div>
                        </div>

                        {requestToView.rejectionReason && (
                            <div>
                              <div className="text-sm text-red-600">Raison du rejet</div>
                              <div className="font-medium bg-red-50 p-3 rounded text-red-700">
                                {requestToView.rejectionReason}
                              </div>
                            </div>
                        )}
                      </div>
                    </div>

                    {/* Actions if pending */}
                    {requestToView.status === "PENDING" && (
                        <div className="flex justify-end space-x-3 pt-4 border-t">
                          <TaleButton
                              variant="outlined"
                              className="!border-red-200 !text-red-600"
                              onClick={() => {
                                setRequestToReject(requestToView);
                                closeViewModal();
                              }}
                          >
                            <FiXCircle className="w-4 h-4 mr-2" />
                            Rejeter
                          </TaleButton>
                          <TaleButton
                              className="bg-green-600 hover:bg-green-700 border-green-600"
                              onClick={() => {
                                handleAccept(requestToView.id);
                                closeViewModal();
                              }}
                          >
                            <FiCheckCircle className="w-4 h-4 mr-2" />
                            Accepter
                          </TaleButton>
                        </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
        )}

        <TaleDashbaordMainCard className={"flex flex-col gap-6"}>
          <div className="gift-requests-header">
            <div className="w-full flex justify-between items-center">
              <div className="flex flex-col">
                <h2 className="!text-2xl font-bold !m-0">Demandes de Cadeaux</h2>
                <p className="text-gray-600">
                  Gérez les demandes de cadeaux des utilisateurs
                </p>
              </div>
              <div className="flex gap-3">
                <TaleButton
                    role="normal"
                    variant="outlined"
                    onClick={() => refetch()}
                >
                  <i className="fas fa-sync-alt mr-2"></i>
                  Actualiser
                </TaleButton>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="flex gap-2">
              <select
                  className="px-4 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={statusFilter}
                  onChange={(e) => handleFilterChange(e.target.value)}
              >
                {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                ))}
              </select>
            </div>

            <div className="ml-auto text-sm text-gray-600">
              {pagination.totalItems} demande(s) trouvée(s)
            </div>
          </div>

          {/* Stats Summary */}
          {!loading && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-white from-blue-50 to-blue-100 p-4 rounded-xl border">
                  <div className="text-sm text-blue-700 font-medium">Total Demandes</div>
                  <div className="text-2xl font-bold text-gray-900">{itemStats.total}</div>
                </div>
                <div className="bg-white from-yellow-50 to-yellow-100 p-4 rounded-xl border">
                  <div className="text-sm text-yellow-700 font-medium">En Attente</div>
                  <div className="text-2xl font-bold text-gray-900">{itemStats.pending} <span className="text-xs font-light text-yellow-600 mt-1">À traiter</span></div>
                </div>
                <div className="bg-white from-green-50 to-green-100 p-4 rounded-xl border">
                  <div className="text-sm text-green-700 font-medium">Acceptées</div>
                  <div className="text-2xl font-bold text-gray-900">{itemStats.accepted}</div>
                </div>
                <div className="bg-white from-red-50 to-red-100 p-4 rounded-xl border">
                  <div className="text-sm text-red-700 font-medium">Rejetées</div>
                  <div className="text-2xl font-bold text-gray-900">{itemStats.rejected}</div>
                </div>
                <div className="bg-white from-purple-50 to-purple-100 p-4 rounded-xl border">
                  <div className="text-sm text-purple-700 font-medium">Points totaux</div>
                  <div className="text-2xl font-bold text-gray-900">{itemStats.totalPoints} <span className="text-xs font-light text-purple-600 mt-1">points utilisés</span></div>
                </div>
              </div>
          )}

          {/* Requests List */}
          <div
              className={`flex flex-col relative grow size-full ${
                  !requestItems.length && !loading ? 'items-center justify-center gap-4 min-h-[400px]' : "gap-4"
              }`}
          >
            {loading && loadingItem}
            {error && !loading ? (
                <div className="size-full z-20 absolute flex items-center justify-center grow">
                  <InternalServerError />
                </div>
            ) : null}
            <Suspense fallback={loadingItem}>
              {loading || error ? null : !requestItems.length ? (
                  <div className="text-center py-12">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-blue-50 to-purple-50 mb-6">
                      <FiGift className="w-10 h-10 text-blue-500" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {statusFilter === "PENDING"
                          ? "Aucune demande en attente"
                          : statusFilter
                              ? `Aucune demande ${statusOptions.find(o => o.value === statusFilter)?.label?.toLowerCase()}`
                              : "Aucune demande"
                      }
                    </h3>
                    <p className="text-gray-600 mb-6">
                      {statusFilter === "PENDING"
                          ? "Toutes les demandes ont été traitées"
                          : "Aucune demande ne correspond aux filtres"
                      }
                    </p>
                    {statusFilter !== "" && (
                        <button
                            onClick={() => handleFilterChange("")}
                            className="px-4 py-2 text-blue-600 hover:text-blue-800 font-medium"
                        >
                          Voir toutes les demandes
                        </button>
                    )}
                  </div>
              ) : (
                  <AdminGiftRequestsList
                      requests={requestItems}
                      updateRequest={updateRequest}
                      onAccept={handleAccept}
                      onRejectClick={handleRejectClick}
                      onViewDetails={handleViewDetails}
                      isProcessing={isProcessing}
                  />
              )}
            </Suspense>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
              <div className="flex justify-center items-center gap-4 mt-6 pt-6 border-t border-gray-200">
                <button
                    className="px-4 py-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50 flex items-center gap-2 transition-colors"
                    disabled={currentPage <= 0}
                    onClick={() => {
                      const params = new URLSearchParams(window.location.search);
                      params.set("page", Math.max(0, currentPage - 1));
                      navigate(`/admin/gift-requests?${params.toString()}`);
                    }}
                >
                  <i className="fas fa-chevron-left"></i>
                  Précédent
                </button>

                <div className="flex items-center gap-2">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum;

                    if (pagination.totalPages <= 5) {
                      pageNum = i;
                    } else if (currentPage < 2) {
                      pageNum = i;
                    } else if (currentPage > pagination.totalPages - 3) {
                      pageNum = pagination.totalPages - 5 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                        <button
                            key={pageNum}
                            className={`w-10 h-10 flex items-center justify-center border rounded-lg transition-all ${
                                currentPage === pageNum
                                    ? "bg-gradient-to-br from-blue-500 to-blue-600 text-white border-blue-500 shadow-sm"
                                    : "hover:bg-gray-50 hover:border-gray-300"
                            }`}
                            onClick={() => {
                              const params = new URLSearchParams(window.location.search);
                              params.set("page", pageNum);
                              navigate(`/admin/gift-requests?${params.toString()}`);
                            }}
                        >
                          {pageNum + 1}
                        </button>
                    );
                  })}

                  {pagination.totalPages > 5 && currentPage < pagination.totalPages - 3 && (
                      <>
                        <span className="mx-1 text-gray-400">...</span>
                        <button
                            className="w-10 h-10 flex items-center justify-center border rounded-lg hover:bg-gray-50 hover:border-gray-300"
                            onClick={() => {
                              const params = new URLSearchParams(window.location.search);
                              params.set("page", pagination.totalPages - 1);
                              navigate(`/admin/gift-requests?${params.toString()}`);
                            }}
                        >
                          {pagination.totalPages}
                        </button>
                      </>
                  )}
                </div>

                <button
                    className="px-4 py-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50 flex items-center gap-2 transition-colors"
                    disabled={currentPage >= pagination.totalPages - 1}
                    onClick={() => {
                      const params = new URLSearchParams(window.location.search);
                      params.set("page", currentPage + 1);
                      navigate(`/admin/gift-requests?${params.toString()}`);
                    }}
                >
                  Suivant
                  <i className="fas fa-chevron-right"></i>
                </button>

                <div className="ml-4 text-sm text-gray-600 hidden md:block">
                  Affichage {Math.min(currentPage * 10 + 1, pagination.totalItems)}-
                  {Math.min((currentPage + 1) * 10, pagination.totalItems)} sur {pagination.totalItems} demandes
                </div>
              </div>
          )}
        </TaleDashbaordMainCard>
      </>
  );
};

export default AdminGiftRequests;
